/**
 * SignupV2 — V2 redesign of signup page.
 * Route: /signup
 *
 * Design: single centred card, light mode, no top nav.
 *
 * IMPORTANT (2026-06-10 fix): Clerk's <SignUp> renders its OWN card with a
 * "Sign up for Primate Vision" header, social buttons, a "Sign in" footer link,
 * etc. Previously this page ALSO wrapped that widget in a second custom card
 * with a duplicate "Create your account" header and a duplicate
 * "Already have an account? Sign in" footer — producing two nested, misaligned
 * cards that overflowed the mobile viewport and truncated the "Optional" field
 * labels ("Optiona"). The fix is to render exactly ONE card: the page chrome is
 * just the brand logo + the Clerk widget, with overflow constrained so the
 * widget can never exceed its container width.
 */

import { SignUp, useAuth } from '@clerk/clerk-react';
import { Link, useSearchParams } from 'react-router';
import { useEffect } from 'react';
import { L } from '../components/V2Nav';
import { useIsMobile } from '../hooks/useIsMobile';

const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const githubOAuthEnabled = import.meta.env.VITE_ENABLE_GITHUB_OAUTH === 'true';

const clerkAppearance = {
  layout: {
    unsafe_disableDevelopmentModeWarnings: true,
  },
  variables: {
    colorBackground: '#FAFAF7',
    colorInputBackground: '#F2F0EB',
    colorText: '#1A1916',
    colorTextSecondary: '#7A7669',
    colorPrimary: '#1B4FE0',
    colorDanger: '#DC2626',
    borderRadius: '8px',
    fontFamily: 'Inter, sans-serif',
  },
  elements: {
    // Clerk's card IS the single card on this page. Give it our surface styling
    // and constrain its width so it never overflows the viewport on mobile
    // (the cause of the truncated "Optional" labels).
    card: {
      boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
      border: `1px solid ${L.border}`,
      background: L.bgSurface,
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
    },
    rootBox: { width: '100%', maxWidth: '100%' },
    cardBox: { width: '100%', maxWidth: '100%' },
    socialButtonsBlockButton__github: githubOAuthEnabled
      ? { background: '#F6F8FA', border: '1px solid #D0D7DE', color: '#24292F' }
      : { display: 'none' },
    socialButtonsBlockButton__google: { background: '#FFFFFF', border: '1px solid #DADCE0', color: '#3C4043' },
    formButtonPrimary: 'font-semibold',
    footerActionLink: 'font-medium',
  },
};

function SignupCard() {
  const { isLoaded } = useAuth();
  if (!isLoaded) return <div style={{ minHeight: 400 }} />;

  return <SignUp afterSignUpUrl="/onboarding" appearance={clerkAppearance} />;
}

export default function SignupV2() {
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan');
  const isMobile = useIsMobile();

  // Force light mode
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'light');
    return () => { document.documentElement.setAttribute('data-theme', prev ?? 'dark'); };
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: L.bgPrimary,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: isMobile ? '32px 16px' : '40px 24px',
      boxSizing: 'border-box',
    }}>
      {/* Brand logo (the only header on the page) */}
      <a href="/v2" style={{ marginBottom: 28, display: 'block' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <img src="/Darwin_v3_vinyl_transparent.png" alt="" style={{ height: 28, width: 'auto', objectFit: 'contain' }} />
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15 }}>
            <span style={{ fontSize: 13, fontWeight: 300, color: '#1A1916', letterSpacing: '-0.02em' }}>Primate</span>
            <span style={{ fontSize: 13, fontWeight: 300, color: '#2058F0', letterSpacing: '-0.02em' }}>Intelligence</span>
          </div>
        </div>
      </a>

      {/* Plan badge if coming from pricing */}
      {plan && (
        <div style={{
          marginBottom: 16,
          padding: '6px 14px', borderRadius: 20,
          background: `${L.signal}14`, border: `1px solid ${L.signal}40`,
          fontSize: 12, fontWeight: 600, color: L.signal,
          fontFamily: '"JetBrains Mono", monospace', letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>
          {plan.charAt(0).toUpperCase() + plan.slice(1)} plan
        </div>
      )}

      {/* Single card — the Clerk widget renders its own bordered card (styled via
          clerkAppearance). No outer wrapper card, so there is exactly one card
          and no nested-overflow / duplicate-header problem. */}
      <div style={{
        width: '100%',
        maxWidth: isMobile ? '100%' : 420,
        boxSizing: 'border-box',
      }}>
        {!clerkKey ? (
          <div style={{
            background: L.bgSurface,
            border: `1px solid ${L.border}`,
            borderRadius: 16,
            padding: '28px 24px',
            textAlign: 'center', fontSize: 13, color: L.textMuted,
          }}>
            Auth not configured.{' '}
            <code style={{ color: L.signal }}>VITE_CLERK_PUBLISHABLE_KEY</code> missing.
          </div>
        ) : (
          <SignupCard />
        )}
      </div>

      {/* Single back affordance. Account/sign-in link is provided by the Clerk
          card itself, so we do NOT duplicate an "Already have an account?" row. */}
      <p style={{ marginTop: 20, fontSize: 13, color: L.textMuted }}>
        <Link to="/v2" style={{ color: L.textMuted, textDecoration: 'none' }}>← Back to Primate Vision</Link>
      </p>
    </div>
  );
}
